import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import ChatList, { ChatKindIcon, chatTitle } from "./ChatList";

/**
 * The chat title in the Copilot header IS the way to your other chats: click it
 * for a searchable list grouped by day, each row showing what that chat produced.
 * Replaces the always-on history sidebar.
 */
const ChatSwitcher = observer(() => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    copilotStore.loadConversations();
    const close = e => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const all = copilotStore.conversations || [];
  const query = q.trim().toLowerCase();
  const items = query
    ? all.filter(c => `${chatTitle(c)} ${c.summary || ""}`.toLowerCase().includes(query))
    : all;
  const current = all.find(c => c.id === copilotStore.conversationId);
  const hasChat = copilotStore.messages.length > 0;
  const label = hasChat ? copilotStore.conversationTitle || (current && current.title) || "This chat" : "Chats";

  const openChat = c => {
    setOpen(false);
    setQ("");
    if (c.id !== copilotStore.conversationId) history.push(`/copilot/${c.id}`);
  };
  const onKey = e => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setFocus(f => Math.min(items.length - 1, f + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setFocus(f => Math.max(0, f - 1)); }
    if (e.key === "Enter" && items[focus]) openChat(items[focus]);
  };

  return (
    <div className="cpSwitcher" ref={boxRef}>
      <span className="cpSwitcher__slash" aria-hidden="true">/</span>
      <button
        type="button"
        className={`cpSwitcher__btn${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => { setOpen(o => !o); setFocus(0); }}
      >
        {hasChat ? <ChatKindIcon kind={(current && current.kind) || "chat"} size="sm" /> : <i className="material-icons-outlined cpSwitcher__hist">history</i>}
        <span className="cpSwitcher__label">{label}</span>
        <i className="material-icons-outlined cpSwitcher__chev">expand_more</i>
      </button>
      {open && (
        <div className="cpSwitcher__pop">
          <label className="cpSwitcher__search">
            <i className="material-icons-outlined">search</i>
            <input
              autoFocus
              value={q}
              placeholder="Search your chats"
              aria-label="Search your chats"
              onChange={e => { setQ(e.target.value); setFocus(0); }}
              onKeyDown={onKey}
            />
          </label>
          <div className="cpSwitcher__body">
            {!all.length && <div className="cpSwitcher__empty"><b>Your chats will live here</b><span>Everything Copilot finds, drafts or builds stays one click away.</span></div>}
            {!!all.length && !items.length && <div className="cpSwitcher__empty"><span>No chats match "{q}".</span></div>}
            <ChatList
              items={items}
              activeIndex={focus}
              onOpen={openChat}
              editable
              onDeleted={wasActive => { if (wasActive) { setOpen(false); history.push("/copilot/new"); } }}
            />
          </div>
          <div className="cpSwitcher__foot"><span>↑↓ to move · ↵ to open · esc to close</span></div>
        </div>
      )}
    </div>
  );
});

export default ChatSwitcher;
